import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import SignaturePad from 'signature_pad';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button, Text } from '@/components/flup';
import BuildProperties from '@/envconfig/BuildProperties';

/**
 * The vendor agreement a venue has to be signed against before its onboarding
 * fee can be paid. Shared by the go-live action on the venue list and the last
 * step of onboarding, so both gate payment on the same signature.
 */

interface VendorAgreementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: string | null;
  /** Fired once the backend has filed the signature. */
  onSigned: () => void;
}

const VendorAgreementModal: React.FC<VendorAgreementModalProps> = ({
  open,
  onOpenChange,
  venueId,
  onSigned,
}) => {
  const sigPadRef = useRef<SignaturePad | null>(null);

  const [isAgreementLoading, setIsAgreementLoading] = useState(false);
  const [isSignAgreementLoading, setIsSignAgreementLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (open) setAccepted(false);
  }, [open]);

  const previewAgreement = async () => {
    if (!venueId) return;
    const token = sessionStorage.getItem('bearer_token');
    setIsAgreementLoading(true);
    try {
      const res = await axios.get(
        `${BuildProperties.getBaseUrl()}/api/vendor-agreement/preview?venueId=${venueId}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } },
      );
      const fileURL = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(fileURL, '_blank');
    } catch (err) {
      console.error('Preview failed', err);
      toast.error('Could not open the agreement');
    } finally {
      setIsAgreementLoading(false);
    }
  };

  const handleSignAgreement = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      toast.error('Please provide a signature');
      return;
    }

    setIsSignAgreementLoading(true);
    try {
      const res = await axios.post(
        `${BuildProperties.getBaseUrl()}/api/vendor-agreement/sign`,
        { signature: sigPadRef.current.toDataURL(), venueId },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('bearer_token')}` } },
      );
      if (res.data.success) {
        onOpenChange(false);
        onSigned();
      }
    } catch (err) {
      console.error('Sign failed', err);
      toast.error('Something went wrong while signing');
    } finally {
      setIsSignAgreementLoading(false);
    }
  };

  /**
   * The signature canvas has to be sized to the device pixel ratio. The dialog
   * unmounts its content on close, so the pad is dropped with the canvas and
   * rebuilt against the fresh one next time it opens.
   */
  const setCanvasRef = (node: HTMLCanvasElement | null) => {
    if (!node) {
      sigPadRef.current?.off();
      sigPadRef.current = null;
      return;
    }
    if (sigPadRef.current) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    node.width = node.offsetWidth * ratio;
    node.height = node.offsetHeight * ratio;
    node.getContext('2d')?.scale(ratio, ratio);

    sigPadRef.current = new SignaturePad(node, { penColor: 'black' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Vendor agreement</DialogTitle>
          <DialogDescription>Review and sign the agreement to continue.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          <Button
            variant="secondary"
            loading={isAgreementLoading}
            disabled={isSignAgreementLoading}
            onClick={previewAgreement}
          >
            View agreement PDF
          </Button>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="w-4 h-4 accent-[#18B48F]"
            />
            <Text variant="body" as="span">
              I accept the terms &amp; conditions
            </Text>
          </label>

          <div>
            <Label>Signature *</Label>
            <canvas
              ref={setCanvasRef}
              className="border border-flup-hairline w-full h-40 rounded-flup-md bg-flup-surface touch-none mt-1.5"
            />
            <div className="flex justify-end mt-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={isSignAgreementLoading}
                onClick={() => sigPadRef.current?.clear()}
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            fullWidth
            loading={isSignAgreementLoading}
            disabled={!accepted || isAgreementLoading}
            onClick={handleSignAgreement}
          >
            Sign &amp; continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VendorAgreementModal;
